import React from 'react';
import { Space, Tooltip, Switch, Typography, Tag } from 'antd';
import { DeleteTwoTone } from '@ant-design/icons';
import { get } from 'lodash';
import moment from 'moment';
import {
  ROLE_ADMIN,
  ROLE_MANAGER,
  ROLE_MARKETING_ADMIN,
  ROLE_MARKETING_MANAGER,
} from './roles';

const { Text } = Typography;

export const campaignTableColumns = (
  onViewDetails,
  updateCampaignStatus,
  onConfirmDelete
) => [
  {
    title: 'Campaign Type',
    key: 'campaign_type',
    dataIndex: 'type',
  },
  {
    title: 'Campaign',
    key: 'campaign_name',
    dataIndex: 'name',
    render: (text, record) => (
      <Text
        className="text-theme-color"
        onClick={() => {
          onViewDetails(record);
        }}
      >
        {text}
      </Text>
    ),
  },
  {
    title: 'Brand',
    key: 'brand',
    dataIndex: ['Company', 'name'],
  },
  {
    title: 'Offering Code',
    key: 'offering_code',
    dataIndex: ['Offering', 'code'],
  },
  {
    title: 'Channel',
    key: 'channel',
    dataIndex: ['SalesChannel', 'name'],
  },
  {
    title: 'Retailer',
    key: 'retailer_count',
    dataIndex: 'retailer_count',
  },
  {
    title: 'Location',
    key: 'location_count',
    dataIndex: 'location_count',
  },
  {
    title: 'Touchpoint',
    key: 'touchpoint_count',
    dataIndex: 'touchpoint_count',
  },
  {
    title: 'Status',
    key: 'is_active',
    render: (record) => (
      <Switch
        checked={get(record, 'is_active')}
        onChange={(checked) => {
          updateCampaignStatus(
            get(record, 'id'),
            get(record, 'company_id'),
            checked
          );
        }}
      />
    ),
  },
  {
    title: 'Date Created',
    key: 'date_created',
    dataIndex: 'created_at',
    render: (item) => (
      <Text>{moment(item).format('DD/MM/YYYY (hh:mm A)')}</Text>
    ),
  },
  {
    title: 'Action',
    key: 'action',
    render: (record) => (
      <Space size="large">
        <Tooltip title="Delete Campaign">
          <DeleteTwoTone
            className="table-icon"
            twoToneColor="#009EA8"
            onClick={() => {
              onConfirmDelete(record);
            }}
          />
        </Tooltip>
      </Space>
    ),
  },
];

export const campaignTableFiltersList = (
  userComapnies,
  channelList,
  touchpointCitiesList,
  offeringCodeList
) => [
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Brand',
    options: userComapnies,
    optionLabelKey: 'Company.name',
    optionValueKey: 'Company.id',
    dropdownKey: 'brand',
    chipValueKey: 'company_id',
    permissions: [ROLE_MARKETING_ADMIN, ROLE_MARKETING_MANAGER],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Channel',
    options: channelList,
    optionLabelKey: 'name',
    optionValueKey: 'id',
    dropdownKey: 'channel',
    chipValueKey: 'sales_channel_id',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Location',
    options: touchpointCitiesList,
    optionLabelKey: 'name',
    optionValueKey: 'id',
    dropdownKey: 'touchpointCities',
    chipValueKey: 'touchpoint_city_id',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Status',
    options: [
      {
        value: true,
        label: 'Active',
      },
      {
        value: false,
        label: 'Inactive',
      },
    ],
    optionLabelKey: 'label',
    optionValueKey: 'value',
    dropdownKey: 'status',
    chipValueKey: 'is_active',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Offering Code',
    options: offeringCodeList,
    optionLabelKey: 'code',
    optionValueKey: 'id',
    dropdownKey: 'offeringCode',
    chipValueKey: 'offering_id',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
];

export const sampleCampaigndetails = () => {
  return [
    {
      title: 'Customer Name',
      key: 'name',
      dataIndex: ['OrderCustomer', 'name'],
    },
    {
      title: 'Mobile No.',
      key: 'mobile',
      dataIndex: ['OrderCustomer', 'mobile'],
    },
    {
      title: 'Email',
      key: 'email',
      dataIndex: ['OrderCustomer', 'email'],
      render: (item) => <Text>{item || '--'}</Text>,
    },
    {
      title: 'Age',
      key: 'age',
      dataIndex: ['OrderCustomer', 'age'],
    },
    {
      title: 'Gender',
      key: 'gender',
      dataIndex: ['OrderCustomer', 'gender'],
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: ['Touchpoint', 'TouchpointCity', 'name'],
    },
    {
      title: 'Touchpoint',
      key: 'touchpoint',
      dataIndex: ['Touchpoint', 'name'],
    },
    {
      title: 'Date',
      key: 'date',
      dataIndex: 'created_at',
      render: (item) => (
        <Text>{moment(item).format('DD/MM/YYYY (hh:mm A)')}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'campaignStatus',
      dataIndex: 'status',
      render: (item) => (
        <Tag color={item ? 'green' : 'red'}>{item ? 'Success' : 'Failure'}</Tag>
      ),
    },
  ];
};

export const retailCampaigndetails = () => {
  return [
    {
      title: 'Retailer',
      key: 'retailer',
      dataIndex: ['Touchpoint', 'PartnerRetailer', 'name'],
    },
    {
      title: 'Product Name',
      key: 'product_name',
      dataIndex: ['Product', 'name'],
    },
    {
      title: 'Location',
      key: 'location',
      dataIndex: ['Touchpoint', 'TouchpointCity', 'name'],
    },
    {
      title: 'Touchpoint',
      key: 'touchpoint',
      dataIndex: ['Touchpoint', 'name'],
    },
  ];
};

export const campaignDetailsTableFiltersList = (
  locationsList,
  touchpointsList
) => [
  {
    type: 'DatePicker',
    dropdownKey: 'dateRange',
    chipValueKey: 'created_at',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Gender',
    options: [
      {
        value: 'M',
        label: 'Male',
      },
      {
        value: 'F',
        label: 'Female',
      },
    ],
    optionLabelKey: 'label',
    optionValueKey: 'value',
    dropdownKey: 'gender',
    chipValueKey: 'gender',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Input',
    subType: 'number',
    addonBefore: 'Age From',
    chipValueKey: 'age_from',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Input',
    subType: 'number',
    addonBefore: 'Age to',
    chipValueKey: 'age_upto',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Status',
    options: [
      {
        value: true,
        label: 'Success',
      },
      {
        value: false,
        label: 'Failure',
      },
    ],
    optionLabelKey: 'label',
    optionValueKey: 'value',
    dropdownKey: 'status',
    chipValueKey: 'status',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Location',
    options: locationsList,
    optionLabelKey: 'name',
    optionValueKey: 'id',
    dropdownKey: 'location',
    chipValueKey: 'touchpoint_city_id',
    isAnyFilterDependent: true,
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
  {
    type: 'Select',
    multipleMode: true,
    placeholder: 'Touchpoint',
    options: touchpointsList,
    optionLabelKey: 'name',
    optionValueKey: 'id',
    dropdownKey: 'touchpoints',
    chipValueKey: 'touchpoint_id',
    permissions: [
      ROLE_ADMIN,
      ROLE_MANAGER,
      ROLE_MARKETING_ADMIN,
      ROLE_MARKETING_MANAGER,
    ],
  },
];
